import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCardIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  XMarkIcon,
  UserIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import {
  billingService,
  Invoice,
  BillingSummary,
  ClinicalTreatmentBilling,
  PaymentRecord,
  CreateInvoiceRequest,
  CreatePaymentRequest,
  InvoiceItem,
  BillingDocument,
} from '../../services/billingService';
import { patientService, Patient } from '../../services/patientService';

// ── Currency formatting using practice settings ─────────
const formatCurrency = (amount: number) => {
  return `${amount.toFixed(2)} Dh`;
};

// ── Status badge colors ─────────────────────────────────
const statusColors: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  Outstanding: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Partial: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  Paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  Overdue: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  Cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

// ── Treatment status colors ─────────────────────────────
const treatmentStatusColors: Record<string, string> = {
  PLANNED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  COMPLETED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

// ── Modal wrapper component ─────────────────────────────
function Modal({ open, onClose, title, children, wide }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; wide?: boolean
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-card border border-border rounded-2xl shadow-2xl ${wide ? 'max-w-4xl' : 'max-w-xl'} w-full max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-card-foreground">{title}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted transition-colors">
            <XMarkIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Main BillingClient Component
// ═══════════════════════════════════════════════════════════

export default function BillingClient() {
  const { t } = useTranslation(['billing', 'common']);

  // ── State ─────────────────────────────────────────────
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [treatments, setTreatments] = useState<ClinicalTreatmentBilling[]>([]);
  const [documents, setDocuments] = useState<BillingDocument[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'invoices' | 'payments' | 'treatments' | 'documents'>('invoices');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState('');

  // ── Load data ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, invoicesData, paymentsData, treatmentsData, patientsData, documentsData] = await Promise.all([
        billingService.getBillingSummary().catch(() => null),
        billingService.getInvoices().catch(() => []),
        billingService.getPayments().catch(() => []),
        billingService.getTreatments().catch(() => []),
        patientService.getPatients().catch(() => []),
        billingService.getDocuments().catch(() => []),
      ]);
      setSummary(summaryData);
      setInvoices(invoicesData);
      setPayments(paymentsData);
      setTreatments(treatmentsData);
      setPatients(patientsData);
      setDocuments(documentsData);
    } catch (e) {
      console.error('Failed to load billing data', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── View invoice detail ───────────────────────────────
  const handleViewInvoice = async (id: string) => {
    try {
      const inv = await billingService.getInvoice(id);
      setViewingInvoice(inv);
    } catch { /* ignore */ }
  };

  // ── Delete invoice ────────────────────────────────────
  const handleDeleteInvoice = async (id: string) => {
    if (!confirm(t('invoice.confirmDelete', 'Delete this invoice?'))) return;
    try {
      await billingService.deleteInvoice(id);
      loadData();
    } catch { /* ignore */ }
  };

  // ── Record payment shortcut ───────────────────────────
  const openPaymentFor = (invoiceId: string) => {
    setPaymentInvoiceId(invoiceId);
    setShowRecordPayment(true);
  };

  // ── Filter logic ──────────────────────────────────────
  const filteredInvoices = invoices.filter(inv => {
    const matchSearch = !searchTerm ||
      inv.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredPayments = payments.filter(p =>
    !searchTerm ||
    (p.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTreatments = treatments.filter(t => {
    const matchSearch = !searchTerm ||
      t.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.act_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.act_label.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── Loading state ─────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-3"></div>
          <p className="text-muted-foreground text-sm">{t('common:loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ══════════════════ HEADER ══════════════════ */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">{t('title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateInvoice(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
          >
            <PlusIcon className="h-4 w-4" />
            {t('createInvoice')}
          </button>
          <button
            onClick={() => { setPaymentInvoiceId(''); setShowRecordPayment(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-card-foreground text-sm font-medium hover:bg-muted transition-colors"
          >
            <BanknotesIcon className="h-4 w-4" />
            {t('newPayment')}
          </button>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-muted-foreground text-sm hover:bg-muted transition-colors"
            title={t('refresh', 'Refresh')}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ══════════════════ SUMMARY CARDS ══════════════════ */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label={t('stats.totalRevenue', 'Revenue YTD')}
            value={formatCurrency(summary.total_revenue_ytd)}
            icon={<ChartBarIcon className="h-5 w-5" />}
            color="text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400"
          />
          <SummaryCard
            label={t('stats.paidInvoices', 'Paid This Month')}
            value={formatCurrency(summary.total_paid_this_month)}
            icon={<CheckCircleIcon className="h-5 w-5" />}
            color="text-sky-600 bg-sky-50 dark:bg-sky-900/20 dark:text-sky-400"
          />
          <SummaryCard
            label={t('stats.pendingPayments', 'Outstanding')}
            value={formatCurrency(summary.total_outstanding)}
            icon={<ClockIcon className="h-5 w-5" />}
            color="text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
          />
          <SummaryCard
            label={t('stats.overdueInvoices', 'Overdue')}
            value={formatCurrency(summary.total_overdue)}
            icon={<ExclamationTriangleIcon className="h-5 w-5" />}
            color="text-rose-600 bg-rose-50 dark:bg-rose-900/20 dark:text-rose-400"
          />
        </div>
      )}

      {/* ══════════════════ TAB NAVIGATION ══════════════════ */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="border-b border-border px-2">
          <nav className="flex" aria-label="Billing tabs">
            {([
              { id: 'invoices', label: t('tabs.invoices', 'Invoices'), icon: DocumentTextIcon, count: invoices.length },
              { id: 'payments', label: t('tabs.payments', 'Payments'), icon: CreditCardIcon, count: payments.length },
              { id: 'treatments', label: t('tabs.treatments', 'Treatments'), icon: DocumentCheckIcon, count: treatments.length },
              { id: 'documents', label: t('tabs.documents', 'Documents'), icon: ArrowDownTrayIcon, count: documents.length },
            ] as const).map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setStatusFilter('all'); }}
                className={`relative flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-card-foreground'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* ── Search + Filter Bar ── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-border bg-muted/30">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('searchPlaceholder', 'Search...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-foreground placeholder:text-muted-foreground"
            />
          </div>
          {activeTab === 'invoices' && (
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-muted-foreground" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="text-sm bg-background border border-input rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-foreground"
              >
                <option value="all">{t('filters.allStatus')}</option>
                <option value="Draft">{t('status.draft', 'Draft')}</option>
                <option value="Outstanding">{t('filters.outstanding')}</option>
                <option value="Partial">{t('filters.partial')}</option>
                <option value="Paid">{t('filters.paid')}</option>
                <option value="Overdue">{t('filters.overdue', 'Overdue')}</option>
                <option value="Cancelled">{t('status.cancelled', 'Cancelled')}</option>
              </select>
            </div>
          )}
          {activeTab === 'treatments' && (
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-muted-foreground" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="text-sm bg-background border border-input rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-foreground"
              >
                <option value="all">{t('treatmentFilters.allStatus', 'All Status')}</option>
                <option value="PLANNED">{t('treatmentFilters.planned', 'Planned')}</option>
                <option value="IN_PROGRESS">{t('treatmentFilters.inProgress', 'In Progress')}</option>
                <option value="COMPLETED">{t('treatmentFilters.completed', 'Completed')}</option>
                <option value="CANCELLED">{t('treatmentFilters.cancelled', 'Cancelled')}</option>
              </select>
            </div>
          )}
        </div>

        {/* ══════ TAB: INVOICES ══════ */}
        {activeTab === 'invoices' && (
          <div className="overflow-x-auto">
            {filteredInvoices.length === 0 ? (
              <EmptyState icon={<DocumentTextIcon className="h-10 w-10" />} message={t('noInvoices')} />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/40">
                    <th className="px-5 py-3">{t('table.invoiceNumber')}</th>
                    <th className="px-5 py-3">{t('table.patient')}</th>
                    <th className="px-5 py-3 text-right">{t('table.amount')}</th>
                    <th className="px-5 py-3 text-right">{t('table.paid')}</th>
                    <th className="px-5 py-3 text-right">{t('table.balance')}</th>
                    <th className="px-5 py-3">{t('table.status')}</th>
                    <th className="px-5 py-3">{t('table.dueDate')}</th>
                    <th className="px-5 py-3 text-right">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredInvoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-5 py-3.5">
                        <span className="font-semibold text-card-foreground">{inv.invoice_number}</span>
                        <div className="text-xs text-muted-foreground">{new Date(inv.date).toLocaleDateString()}</div>
                      </td>
                      <td className="px-5 py-3.5 font-medium text-card-foreground">{inv.patient_name}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-card-foreground">{formatCurrency(inv.total_amount)}</td>
                      <td className="px-5 py-3.5 text-right text-emerald-600 dark:text-emerald-400">{formatCurrency(inv.amount_paid)}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-card-foreground">{formatCurrency(inv.amount_due)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColors[inv.status] || statusColors.Draft}`}>
                          {t(`status.${inv.status.toLowerCase()}`, inv.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground">
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleViewInvoice(inv.id)} className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title={t('tooltips.viewInvoice')}>
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                            <button onClick={() => openPaymentFor(inv.id)} className="p-1.5 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-muted-foreground hover:text-emerald-600 transition-colors" title={t('payment.record')}>
                              <BanknotesIcon className="h-4 w-4" />
                            </button>
                          )}
                          {(inv.status === 'Draft' || inv.status === 'Cancelled') && (
                            <button onClick={() => handleDeleteInvoice(inv.id)} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" title={t('tooltips.deleteInvoice')}>
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ══════ TAB: PAYMENTS ══════ */}
        {activeTab === 'payments' && (
          <div className="overflow-x-auto">
            {filteredPayments.length === 0 ? (
              <EmptyState icon={<CreditCardIcon className="h-10 w-10" />} message={t('noPayments', 'No payments recorded yet')} />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/40">
                    <th className="px-5 py-3">{t('payment.date', 'Date')}</th>
                    <th className="px-5 py-3">{t('table.patient')}</th>
                    <th className="px-5 py-3">{t('table.invoice', 'Invoice')}</th>
                    <th className="px-5 py-3 text-right">{t('payment.amount')}</th>
                    <th className="px-5 py-3">{t('payment.method')}</th>
                    <th className="px-5 py-3">{t('payment.reference', 'Reference')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPayments.map(p => (
                    <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-5 py-3.5 text-card-foreground">{p.date ? new Date(p.date).toLocaleDateString() : '—'}</td>
                      <td className="px-5 py-3.5 font-medium text-card-foreground">{p.patient_name || '—'}</td>
                      <td className="px-5 py-3.5 text-muted-foreground font-mono text-xs">{p.invoice_number || '—'}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(p.amount)}</td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex px-2 py-0.5 rounded-md bg-muted text-xs font-medium text-muted-foreground">{p.method}</span>
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">{p.receipt_number || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ══════ TAB: TREATMENTS ══════ */}
        {activeTab === 'treatments' && (
          <div className="overflow-x-auto">
            {filteredTreatments.length === 0 ? (
              <EmptyState icon={<DocumentCheckIcon className="h-10 w-10" />} message={t('noTreatments', 'No treatments found')} />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/40">
                    <th className="px-5 py-3">{t('table.code', 'Code')}</th>
                    <th className="px-5 py-3">{t('table.act', 'Act')}</th>
                    <th className="px-5 py-3">{t('table.patient')}</th>
                    <th className="px-5 py-3">{t('table.tooth', 'Tooth')}</th>
                    <th className="px-5 py-3 text-right">{t('table.amount')}</th>
                    <th className="px-5 py-3">{t('table.status')}</th>
                    <th className="px-5 py-3">{t('table.invoiced', 'Invoiced')}</th>
                    <th className="px-5 py-3">{t('table.date')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredTreatments.map(tr => (
                    <tr key={tr.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-5 py-3.5 font-mono text-xs font-semibold text-card-foreground">{tr.act_code || '—'}</td>
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-card-foreground">{tr.act_label}</div>
                        <div className="text-xs text-muted-foreground">{tr.category_name}</div>
                      </td>
                      <td className="px-5 py-3.5 text-card-foreground">{tr.patient_name}</td>
                      <td className="px-5 py-3.5 text-muted-foreground">{tr.tooth_number || '—'}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-card-foreground">{formatCurrency(tr.amount)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${treatmentStatusColors[tr.status] || 'bg-muted text-muted-foreground'}`}>
                          {t(`treatmentStatus.${tr.status}`, tr.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {tr.is_invoiced ? (
                          <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">
                        {tr.completed_date
                          ? new Date(tr.completed_date).toLocaleDateString()
                          : tr.planned_date
                          ? new Date(tr.planned_date).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ══════ TAB: DOCUMENTS ══════ */}
        {activeTab === 'documents' && (
          <div className="overflow-x-auto">
            {documents.length === 0 ? (
              <EmptyState icon={<ArrowDownTrayIcon className="h-10 w-10" />} message={t('documents.empty', 'No generated documents yet')} />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/40">
                    <th className="px-5 py-3">{t('documents.type', 'Type')}</th>
                    <th className="px-5 py-3">{t('documents.fileName', 'File Name')}</th>
                    <th className="px-5 py-3">{t('table.patient')}</th>
                    <th className="px-5 py-3">{t('documents.invoice', 'Invoice')}</th>
                    <th className="px-5 py-3">{t('documents.size', 'Size')}</th>
                    <th className="px-5 py-3">{t('documents.generatedAt', 'Generated')}</th>
                    <th className="px-5 py-3 text-center">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {documents.map(doc => (
                    <tr key={doc.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          doc.document_type === 'Invoice'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : doc.document_type === 'CNOPS'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}>
                          {doc.document_type === 'Invoice' ? t('documents.typeInvoice', 'Facture')
                            : doc.document_type === 'CNOPS' ? t('documents.typeCNOPS', 'Fiche CNOPS')
                            : doc.document_type === 'Estimate' ? t('documents.typeEstimate', 'Devis')
                            : doc.document_type}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-card-foreground text-xs">{doc.file_name}</div>
                        <div className="text-xs text-muted-foreground uppercase">{doc.file_format}</div>
                      </td>
                      <td className="px-5 py-3.5 text-card-foreground">{doc.patient_name || '—'}</td>
                      <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">{doc.invoice_number || '—'}</td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">
                        {doc.file_size > 1048576
                          ? `${(doc.file_size / 1048576).toFixed(1)} MB`
                          : doc.file_size > 1024
                          ? `${(doc.file_size / 1024).toFixed(0)} KB`
                          : `${doc.file_size} B`}
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">
                        {doc.generated_at ? new Date(doc.generated_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {doc.invoice_id && (
                            <button
                              onClick={async () => {
                                try {
                                  if (doc.document_type === 'Invoice') {
                                    await billingService.regenerateInvoiceExcel(doc.invoice_id!);
                                  } else if (doc.document_type === 'CNOPS') {
                                    await billingService.regenerateCNOPSForm(doc.invoice_id!);
                                  }
                                } catch (err: any) {
                                  alert(err.message || 'Error');
                                }
                              }}
                              title={t('documents.redownload', 'Re-download')}
                              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-card-foreground transition-colors"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!confirm(t('documents.deleteConfirm', 'Delete this document record?'))) return;
                              try {
                                await billingService.deleteDocument(doc.id);
                                loadData();
                              } catch { /* ignore */ }
                            }}
                            title={t('documents.delete', 'Delete')}
                            className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════ MODALS ═══════════════════ */}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        open={showCreateInvoice}
        onClose={() => setShowCreateInvoice(false)}
        patients={patients}
        onCreated={() => { setShowCreateInvoice(false); loadData(); }}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        open={showRecordPayment}
        onClose={() => setShowRecordPayment(false)}
        invoices={invoices}
        preselectedInvoiceId={paymentInvoiceId}
        onRecorded={() => { setShowRecordPayment(false); loadData(); }}
      />

      {/* View Invoice Detail Modal */}
      <InvoiceDetailModal
        invoice={viewingInvoice}
        onClose={() => setViewingInvoice(null)}
        onPayment={id => { setViewingInvoice(null); openPaymentFor(id); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

function SummaryCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xl font-bold text-card-foreground truncate">{value}</p>
          <p className="text-xs font-medium text-muted-foreground mt-0.5">{label}</p>
        </div>
        <div className={`flex-shrink-0 p-2.5 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="mx-auto mb-3 text-muted-foreground/40">{icon}</div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Create Invoice Modal
// ═══════════════════════════════════════════════════════════

function CreateInvoiceModal({ open, onClose, patients, onCreated }: {
  open: boolean;
  onClose: () => void;
  patients: Patient[];
  onCreated: () => void;
}) {
  const { t } = useTranslation(['billing']);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [unbilledTreatments, setUnbilledTreatments] = useState<ClinicalTreatmentBilling[]>([]);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<string[]>([]);
  const [manualItems, setManualItems] = useState<Array<{ description: string; unit_price: number; quantity: number }>>([]);
  const [invoiceType, setInvoiceType] = useState<'Final' | 'Estimate'>('Final');
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingTreatments, setLoadingTreatments] = useState(false);

  // Load unbilled treatments when patient changes
  useEffect(() => {
    if (!selectedPatientId) { setUnbilledTreatments([]); return; }
    setLoadingTreatments(true);
    billingService.getUnbilledTreatments(selectedPatientId)
      .then(setUnbilledTreatments)
      .catch(() => setUnbilledTreatments([]))
      .finally(() => setLoadingTreatments(false));
    setSelectedTreatmentIds([]);
  }, [selectedPatientId]);

  const toggleTreatment = (id: string) => {
    setSelectedTreatmentIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const addManualItem = () => {
    setManualItems(prev => [...prev, { description: '', unit_price: 0, quantity: 1 }]);
  };

  const removeManualItem = (idx: number) => {
    setManualItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateManualItem = (idx: number, field: string, val: any) => {
    setManualItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  // Calculate totals
  const treatmentTotal = unbilledTreatments
    .filter(t => selectedTreatmentIds.includes(t.id))
    .reduce((s, t) => s + t.amount, 0);
  const manualTotal = manualItems.reduce((s, item) => s + item.unit_price * item.quantity, 0);
  const subtotal = treatmentTotal + manualTotal;
  const total = subtotal - discount;

  const handleSubmit = async () => {
    if (!selectedPatientId) return;
    setSaving(true);
    try {
      const req: CreateInvoiceRequest = {
        patient_id: selectedPatientId,
        treatment_ids: selectedTreatmentIds,
        items: manualItems.filter(item => item.description && item.unit_price > 0).map(item => ({
          description: item.description,
          unit_price: item.unit_price,
          quantity: item.quantity,
        })),
        invoice_type: invoiceType,
        discount,
        notes,
        finalize: true,
      };
      await billingService.createInvoice(req);
      // Reset
      setSelectedPatientId('');
      setSelectedTreatmentIds([]);
      setManualItems([]);
      setDiscount(0);
      setNotes('');
      onCreated();
    } catch (e) {
      alert('Error creating invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('invoice.new', 'New Invoice')} wide>
      <div className="space-y-6">
        {/* Patient Selection */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('invoice.patient')}</label>
          <select
            value={selectedPatientId}
            onChange={e => setSelectedPatientId(e.target.value)}
            className="w-full border border-input rounded-lg px-3 py-2.5 bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            <option value="">{t('invoice.patientPlaceholder', 'Select patient...')}</option>
            {patients.map(p => (
              <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
            ))}
          </select>
        </div>

        {/* Unbilled Treatments */}
        {selectedPatientId && (
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">{t('invoice.unbilledTreatments', 'Unbilled Treatments')}</label>
            {loadingTreatments ? (
              <div className="text-sm text-muted-foreground py-4 text-center">{t('invoice.loadingTreatments', 'Loading treatments...')}</div>
            ) : unbilledTreatments.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center bg-muted/30 rounded-lg">{t('invoice.noUnbilledTreatments', 'No unbilled treatments for this patient')}</div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                {unbilledTreatments.map(tr => (
                  <label key={tr.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedTreatmentIds.includes(tr.id)}
                      onChange={() => toggleTreatment(tr.id)}
                      className="rounded border-input text-primary focus:ring-primary/30"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-card-foreground">{tr.act_label || tr.act_code}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {tr.tooth_number ? `#${tr.tooth_number}` : ''}
                        {tr.completed_date ? ` · ${new Date(tr.completed_date).toLocaleDateString()}` : ''}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-card-foreground">{formatCurrency(tr.amount)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manual Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-card-foreground">{t('invoice.items', 'Manual Items')}</label>
            <button onClick={addManualItem} className="text-xs text-primary hover:underline flex items-center gap-1">
              <PlusIcon className="h-3 w-3" /> {t('invoice.addItem', 'Add Item')}
            </button>
          </div>
          {manualItems.length > 0 && (
            <div className="space-y-2">
              {manualItems.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={t('invoice.description', 'Description')}
                    value={item.description}
                    onChange={e => updateManualItem(idx, 'description', e.target.value)}
                    className="flex-1 border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <input
                    type="number"
                    placeholder={t('invoice.qty', 'Qty')}
                    value={item.quantity}
                    onChange={e => updateManualItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-16 border border-input rounded-lg px-2 py-2 bg-background text-sm text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <input
                    type="number"
                    placeholder={t('invoice.unitPrice', 'Price')}
                    value={item.unit_price || ''}
                    onChange={e => updateManualItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                    className="w-28 border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground text-right focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <button onClick={() => removeManualItem(idx)} className="p-1.5 text-muted-foreground hover:text-destructive">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoice Options */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('invoice.type', 'Type')}</label>
            <select
              value={invoiceType}
              onChange={e => setInvoiceType(e.target.value as 'Final' | 'Estimate')}
              className="w-full border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="Final">{t('invoice.typeFinal', 'Final')}</option>
              <option value="Estimate">{t('invoice.typeEstimate', 'Estimate')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('invoice.discount', 'Discount')} (Dh)</label>
            <input
              type="number"
              value={discount || ''}
              onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
              className="w-full border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('invoice.notes', 'Notes')}</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Totals */}
        <div className="bg-muted/40 rounded-lg p-4 space-y-1">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{t('invoice.subtotal', 'Subtotal')}</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{t('invoice.discount', 'Discount')}</span>
              <span>-{formatCurrency(discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-card-foreground pt-1 border-t border-border">
            <span>{t('invoice.total', 'Total')}</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors">
            {t('common:cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedPatientId || (selectedTreatmentIds.length === 0 && manualItems.length === 0) || saving}
            className="px-5 py-2 text-sm bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('common:saving', 'Saving...') : t('createInvoice', 'Create Invoice')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════
// Record Payment Modal
// ═══════════════════════════════════════════════════════════

function RecordPaymentModal({ open, onClose, invoices, preselectedInvoiceId, onRecorded }: {
  open: boolean;
  onClose: () => void;
  invoices: Invoice[];
  preselectedInvoiceId: string;
  onRecorded: () => void;
}) {
  const { t } = useTranslation(['billing']);
  const [invoiceId, setInvoiceId] = useState(preselectedInvoiceId);
  const [amount, setAmount] = useState<number>(0);
  const [method, setMethod] = useState('Espèce');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInvoiceId(preselectedInvoiceId);
    // Auto-fill remaining amount
    const inv = invoices.find(i => i.id === preselectedInvoiceId);
    if (inv) setAmount(inv.amount_due);
  }, [preselectedInvoiceId, invoices]);

  const unpaidInvoices = invoices.filter(i => i.status !== 'Paid' && i.status !== 'Cancelled');

  const handleSubmit = async () => {
    if (!invoiceId || amount <= 0) return;
    setSaving(true);
    try {
      await billingService.createPayment({
        invoice_id: invoiceId,
        amount,
        method,
        receipt_number: receiptNumber,
        notes: payNotes,
      });
      setAmount(0);
      setReceiptNumber('');
      setPayNotes('');
      onRecorded();
    } catch { alert('Error recording payment'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('payment.record', 'Record Payment')}>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('table.invoice', 'Invoice')}</label>
          <select
            value={invoiceId}
            onChange={e => {
              setInvoiceId(e.target.value);
              const inv = invoices.find(i => i.id === e.target.value);
              if (inv) setAmount(inv.amount_due);
            }}
            className="w-full border border-input rounded-lg px-3 py-2.5 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">{t('payment.selectInvoice', 'Select invoice...')}</option>
            {unpaidInvoices.map(inv => (
              <option key={inv.id} value={inv.id}>
                {inv.invoice_number} — {inv.patient_name} — {t('payment.due', 'Due')}: {formatCurrency(inv.amount_due)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('payment.amount')} (Dh)</label>
          <input
            type="number"
            value={amount || ''}
            onChange={e => setAmount(parseFloat(e.target.value) || 0)}
            step="0.01"
            className="w-full border border-input rounded-lg px-3 py-2.5 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('payment.method')}</label>
          <select
            value={method}
            onChange={e => setMethod(e.target.value)}
            className="w-full border border-input rounded-lg px-3 py-2.5 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="Espèce">{t('payment.methods.cash', 'Cash / Espèce')}</option>
            <option value="Carte">{t('payment.methods.card', 'Card / Carte')}</option>
            <option value="Chèque">{t('payment.methods.check', 'Check / Chèque')}</option>
            <option value="Virement">{t('payment.methods.bankTransfer', 'Bank Transfer')}</option>
            <option value="Assurance">{t('payment.methods.insurance', 'Insurance')}</option>
            <option value="Autre">{t('payment.methods.other', 'Other')}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('payment.reference', 'Reference Number')}</label>
          <input
            type="text"
            value={receiptNumber}
            onChange={e => setReceiptNumber(e.target.value)}
            className="w-full border border-input rounded-lg px-3 py-2.5 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder={t('payment.optional', 'Optional')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('payment.notes', 'Notes')}</label>
          <textarea
            value={payNotes}
            onChange={e => setPayNotes(e.target.value)}
            rows={2}
            className="w-full border border-input rounded-lg px-3 py-2 bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors">
            {t('common:cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!invoiceId || amount <= 0 || saving}
            className="px-5 py-2 text-sm bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {saving ? t('common:saving', 'Saving...') : t('payment.record', 'Record Payment')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════
// Invoice Detail Modal
// ═══════════════════════════════════════════════════════════

function InvoiceDetailModal({ invoice, onClose, onPayment }: {
  invoice: Invoice | null;
  onClose: () => void;
  onPayment: (invoiceId: string) => void;
}) {
  const { t } = useTranslation(['billing']);
  const [downloadingCNOPS, setDownloadingCNOPS] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  if (!invoice) return null;

  const handleDownloadCNOPS = async () => {
    setDownloadingCNOPS(true);
    try {
      await billingService.downloadCNOPSForm(invoice.id);
    } catch (err: any) {
      alert(err.message || t('common:error', 'Error'));
    } finally {
      setDownloadingCNOPS(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      await billingService.downloadInvoiceExcel(invoice.id);
    } catch (err: any) {
      alert(err.message || t('common:error', 'Error'));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  return (
    <Modal open={!!invoice} onClose={onClose} title={`${t('invoice.title')} ${invoice.invoice_number}`} wide>
      <div className="space-y-6">
        {/* Header Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('invoice.patient')}</p>
            <p className="text-sm font-semibold text-card-foreground mt-0.5">{invoice.patient_name}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('invoice.date')}</p>
            <p className="text-sm font-medium text-card-foreground mt-0.5">{new Date(invoice.date).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('invoice.dueDate')}</p>
            <p className="text-sm font-medium text-card-foreground mt-0.5">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('table.status')}</p>
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${statusColors[invoice.status]}`}>
              {t(`status.${invoice.status.toLowerCase()}`, invoice.status)}
            </span>
          </div>
        </div>

        {/* Items */}
        {invoice.items && invoice.items.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-card-foreground mb-2">{t('invoice.items', 'Items')}</h3>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 text-xs text-muted-foreground uppercase">
                    <th className="px-4 py-2 text-left">{t('table.code', 'Code')}</th>
                    <th className="px-4 py-2 text-left">{t('invoice.description')}</th>
                    <th className="px-4 py-2 text-center">{t('table.tooth', 'Tooth')}</th>
                    <th className="px-4 py-2 text-center">{t('invoice.quantity')}</th>
                    <th className="px-4 py-2 text-right">{t('invoice.unitPrice')}</th>
                    <th className="px-4 py-2 text-right">{t('invoice.amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoice.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 font-mono text-xs">{item.act_code || '—'}</td>
                      <td className="px-4 py-2 text-card-foreground">{item.description}</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">{item.tooth_number || '—'}</td>
                      <td className="px-4 py-2 text-center">{item.quantity}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-card-foreground">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="bg-muted/30 rounded-lg p-4 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('invoice.subtotal')}</span>
            <span className="text-card-foreground">{formatCurrency(invoice.total_amount)}</span>
          </div>
          {invoice.discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('invoice.discount')}</span>
              <span className="text-card-foreground">-{formatCurrency(invoice.discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('table.paid')}</span>
            <span className="text-emerald-600 dark:text-emerald-400">{formatCurrency(invoice.amount_paid)}</span>
          </div>
          <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
            <span className="text-card-foreground">{t('table.balance')}</span>
            <span className={invoice.amount_due > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600'}>
              {formatCurrency(invoice.amount_due)}
            </span>
          </div>
        </div>

        {/* Payments History */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-card-foreground mb-2">{t('invoice.paymentHistory', 'Payment History')}</h3>
            <div className="space-y-2">
              {invoice.payments.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-muted/20 rounded-lg text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-card-foreground">{formatCurrency(p.amount)}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{p.method}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.date ? new Date(p.date).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div>
            <h3 className="text-sm font-semibold text-card-foreground mb-1">{t('invoice.notes')}</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center gap-3 pt-2">
          <div className="flex gap-2">
            <button
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              className="px-4 py-2 text-sm border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-lg font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <PrinterIcon className="h-4 w-4" />
              {downloadingInvoice ? t('common:loading', '...') : t('invoice.downloadInvoice', 'Facture')}
            </button>
            <button
              onClick={handleDownloadCNOPS}
              disabled={downloadingCNOPS}
              className="px-4 py-2 text-sm border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloadingCNOPS ? t('common:loading', '...') : t('invoice.cnopsForm', 'Fiche CNOPS')}
            </button>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              {t('common:close', 'Close')}
            </button>
            {invoice.amount_due > 0 && invoice.status !== 'Cancelled' && (
              <button
                onClick={() => onPayment(invoice.id)}
                className="px-5 py-2 text-sm bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <BanknotesIcon className="h-4 w-4" />
                {t('payment.record', 'Record Payment')}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}